import { useState } from "react";
import { X, Eye, EyeOff, Check } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast, Toaster } from "react-hot-toast";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import Logo from "../../assets/images/logo/hazer-logo.png";
import apiUrls from "../../config/api-urls";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [formData, setFormData] = useState({
    newPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");

  // Password validation rules
  const passwordRules = {
    minLength: formData.newPassword.length >= 6,
    hasUpperCase: /[A-Z]/.test(formData.newPassword),
    hasLowerCase: /[a-z]/.test(formData.newPassword),
    hasNumber: /[0-9]/.test(formData.newPassword),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword),
  };

  const isPasswordValid = Object.values(passwordRules).every(Boolean);

  const validateForm = () => {
    const newErrors = {};

    if (!isPasswordValid) {
      newErrors.newPassword = "Password must meet all requirements";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(apiUrls.auth.resetPassword, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: token,
          newPassword: formData.newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok || data?.error) {
        const backendError =
          data?.error?.message || data?.message || "Failed to reset password";

        toast.error(backendError);

        if (
          backendError.toLowerCase().includes("invalid") ||
          backendError.toLowerCase().includes("expired")
        ) {
          setTimeout(() => {
            navigate("/forgot-password");
          }, 2000);
        }

        throw new Error(backendError);
      }
      const successMessage =
        data?.data?.message || "Password updated successfully!";
      toast.success(successMessage);
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error) {
      const errorMsg =
        error.message || "Failed to reset password. Please try again.";
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
    if (error) {
      setError("");
    }
  };

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="flex flex-col lg:flex-row min-h-[600px]">
            {/* Left Panel */}
            <div className="hidden sm:flex sm:w-1/2 flex-col items-center justify-center text-center p-6 bg-gradient-to-br from-[#ff9906] to-[#f79d2d] text-white">
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                Welcome to Hazer Globals
              </h2>
              <p className="text-sm md:text-lg">
                We make it easy for everyone to access their account.
              </p>
              <div className="bg-white rounded-2xl mt-4">
                <img
                  src={Logo || "/placeholder.svg"}
                  alt="Building"
                  className="mt-6 w-32 h-32 md:w-40 md:h-40 max-w-xs rounded-lg shadow-lg"
                />
              </div>
            </div>

            {/* Right Panel - Reset Password Form */}
            <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
              <div className="w-full max-w-md mx-auto">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Reset Password
                  </h2>
                  <Link to="/login">
                    <X className="w-6 h-6 text-gray-400 hover:text-gray-600 cursor-pointer" />
                  </Link>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="newPassword"
                      className="text-sm font-medium text-gray-700"
                    >
                      New Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showPassword ? "text" : "password"}
                        value={formData.newPassword}
                        onChange={(e) =>
                          handleInputChange("newPassword", e.target.value)
                        }
                        placeholder="Enter new password"
                        required
                        className="w-full px-4 py-3 bg-gray-100 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-[#ff8c00] focus:ring-opacity-50 pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>

                    {/* Password Requirements */}
                    {formData.newPassword && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-2">
                        <p className="text-xs font-medium text-gray-700 mb-2">
                          Password must contain:
                        </p>

                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                passwordRules.minLength
                                  ? "bg-green-500"
                                  : "bg-gray-300"
                              }`}
                            >
                              {passwordRules.minLength && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <span
                              className={`text-xs ${
                                passwordRules.minLength
                                  ? "text-green-600 font-medium"
                                  : "text-gray-600"
                              }`}
                            >
                              At least 6 characters
                            </span>
                          </div>

                          <div className="flex items-center gap-2">
                            <div
                              className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                passwordRules.hasUpperCase
                                  ? "bg-green-500"
                                  : "bg-gray-300"
                              }`}
                            >
                              {passwordRules.hasUpperCase && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <span
                              className={`text-xs ${
                                passwordRules.hasUpperCase
                                  ? "text-green-600 font-medium"
                                  : "text-gray-600"
                              }`}
                            >
                              One uppercase letter (A-Z)
                            </span>
                          </div>

                          <div className="flex items-center gap-2">
                            <div
                              className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                passwordRules.hasLowerCase
                                  ? "bg-green-500"
                                  : "bg-gray-300"
                              }`}
                            >
                              {passwordRules.hasLowerCase && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <span
                              className={`text-xs ${
                                passwordRules.hasLowerCase
                                  ? "text-green-600 font-medium"
                                  : "text-gray-600"
                              }`}
                            >
                              One lowercase letter (a-z)
                            </span>
                          </div>

                          <div className="flex items-center gap-2">
                            <div
                              className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                passwordRules.hasNumber
                                  ? "bg-green-500"
                                  : "bg-gray-300"
                              }`}
                            >
                              {passwordRules.hasNumber && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <span
                              className={`text-xs ${
                                passwordRules.hasNumber
                                  ? "text-green-600 font-medium"
                                  : "text-gray-600"
                              }`}
                            >
                              One number (0-9)
                            </span>
                          </div>

                          <div className="flex items-center gap-2">
                            <div
                              className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                passwordRules.hasSpecialChar
                                  ? "bg-green-500"
                                  : "bg-gray-300"
                              }`}
                            >
                              {passwordRules.hasSpecialChar && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <span
                              className={`text-xs ${
                                passwordRules.hasSpecialChar
                                  ? "text-green-600 font-medium"
                                  : "text-gray-600"
                              }`}
                            >
                              One special character (!@#$%^&*)
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {errors.newPassword && (
                      <p className="text-sm text-red-600">
                        {errors.newPassword}
                      </p>
                    )}
                  </div>

                  {error && (
                    <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={
                      isLoading || !formData.newPassword || !isPasswordValid
                    }
                    className="w-full bg-[#ff8c00] hover:bg-[#e67c00] text-white py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? "Updating Password..." : "Update Password"}
                  </Button>

                  <div className="flex justify-center">
                    <Link to="/login">
                      <Button
                        variant="outline"
                        className="px-8 py-2 rounded-lg bg-transparent"
                      >
                        Back to Login
                      </Button>
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
